/*
 * Proyección del saldo de un comitente para la instrucción que se está cargando.
 *
 * El impacto sale de `impactoInstruccion`, la misma función con la que se calcula
 * Control de Saldos: así el preview del formulario y la planilla nunca se
 * contradicen. Lo único que agrega este módulo es el estado en el que todavía
 * está la carga (programada, rescate total sin monto, moneda sin saldo seguido).
 */

import { impactoInstruccion, MONEDAS_CON_SALDO, TIPOS_INSTR_DINERO } from './balanceCalculator';
import { calcularFechaLiquidacionFci, fromIsoDate } from './dateHelpers';
import { parseAmountForState } from './formatters';

// 'YYYY-MM-DD' a DD/MM/AAAA para texto corrido.
const formatIsoParaTexto = (iso) => (iso ? String(iso).split('-').reverse().join('/') : '');

/*
 * Devuelve:
 * - null si la instrucción no mueve efectivo o todavía no hay comitente elegido.
 * - { sinSaldoSeguido: true, moneda } si la moneda no es una de las que sigue el
 *   snapshot del custodio (hoy pesos, USD Mep y USD Cable son todas seguidas).
 * - { moneda, saldoActual, impacto, saldoPostCarga, montoDesconocido, excluyeEdicion, nota }
 *   en el resto de los casos.
 *
 * `fechaHoy` es la clave YYYY-MM-DD de useFechaHoy: pasarla (y no un new Date())
 * hace que el preview se recalcule solo al cruzar la medianoche.
 */
export const buildSaldoPreview = ({ formState, editingInstruccion, getSaldoComitente, fechaHoy }) => {
    const { tipo, nroComitente, moneda } = formState;
    if (!nroComitente) return null;
    if (!TIPOS_INSTR_DINERO.includes(tipo)) return null;
    if (!MONEDAS_CON_SALDO.includes(moneda)) return { sinSaldoSeguido: true, moneda };

    const excluyeEdicion = !!editingInstruccion?.id;
    // Al editar, la instrucción ya está sumada en el saldo del comitente: se
    // descuenta para que "saldo actual" sea el de antes de esta carga y el impacto
    // no se cuente dos veces.
    const saldoActual = getSaldoComitente(
        nroComitente,
        moneda,
        excluyeEdicion ? { excludeInstruccionId: editingInstruccion.id } : {},
    );
    if (!saldoActual) return null;

    const esRescate = tipo === 'Rescate FCI';
    const esRescateTotal = esRescate && !!formState.esRescateTotal;
    const esProgramada = !!formState.esProgramada && !!formState.fechaProgramada;
    const monto = esRescateTotal ? 0 : (parseFloat(parseAmountForState(formState.montoInputValue)) || 0);
    const plazoLiquidacion = esRescate ? (formState.plazoLiquidacion || 'T+0') : 'T+0';

    // Misma fecha base que el payload que se guarda: el día programado si la
    // instrucción es diferida, el de carga en el resto de los casos.
    const baseDate = esProgramada
        ? fromIsoDate(formState.fechaProgramada)
        : (editingInstruccion?.createdAt?.toDate?.() || fromIsoDate(fechaHoy));
    const fechaLiquidacion = esRescate ? calcularFechaLiquidacionFci(plazoLiquidacion, baseDate) : '';

    // Una instrucción programada nace en estado 'Programada' y recién impacta el
    // día que se desbloquea: hoy el saldo no se mueve.
    const impacto = esProgramada
        ? 0
        : impactoInstruccion({ tipo, monto, plazoLiquidacion, fechaLiquidacion }, fechaHoy);

    let nota = null;
    if (esProgramada) {
        nota = `Queda programada para el ${formatIsoParaTexto(formState.fechaProgramada)}: no modifica el saldo de hoy.`;
    } else if (esRescateTotal) {
        nota = 'Rescate total: el monto lo define la sociedad gerente, todavía no se puede proyectar el saldo.';
    } else if (esRescate && plazoLiquidacion === 'T+1' && fechaLiquidacion > fechaHoy) {
        nota = `Liquida el ${formatIsoParaTexto(fechaLiquidacion)} (T+1): no modifica el saldo de hoy.`;
    }

    return {
        moneda,
        saldoActual,
        impacto,
        saldoPostCarga: saldoActual.saldoProyectado + impacto,
        montoDesconocido: esRescateTotal && !esProgramada,
        excluyeEdicion,
        nota,
    };
};
